// Push Notification Utility Functions
// Complete set of functions for handling push notifications
// Location: src/common/utils/push-notification-utils.js

import { messaging, getToken, onMessage } from './firebase/firebase-init';
import { FCM_VAPID_KEY } from './firebase/firebase-config';

/**
 * Request notification permission from user
 * @returns {Promise<string>} 'granted', 'denied', or 'default'
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('⚠️ This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('⚠️ Notification permission has been denied. Cannot request again programmatically.');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Trigger notification permission request with detailed response
 * @returns {Promise<Object>} Object with success, permission, and message
 */
export const triggerNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return {
      success: false,
      permission: 'denied',
      message: 'This browser does not support notifications'
    };
  }

  const currentPermission = Notification.permission;

  if (currentPermission === 'granted') {
    return {
      success: true,
      permission: 'granted',
      message: 'Notifications are already enabled'
    };
  }

  if (currentPermission === 'denied') {
    return {
      success: false,
      permission: 'denied',
      message: 'Permission was denied. Please enable it in browser settings (click the lock icon in address bar).'
    };
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      return {
        success: true,
        permission: 'granted',
        message: 'Notification permission granted!'
      };
    } else {
      return {
        success: false,
        permission: permission,
        message: 'Notification permission was not granted'
      };
    }
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error);
    return {
      success: false,
      permission: 'denied',
      message: 'An error occurred while requesting permission'
    };
  }
};

/**
 * Ensure Service Worker is registered
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const ensureServiceWorkerRegistered = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Worker is not supported');
    return null;
  }

  try {
    let registration = await navigator.serviceWorker.getRegistration('/');
    
    if (!registration) {
      // Check if service worker file exists
      try {
        const swUrl = '/firebase-messaging-sw.js';
        const response = await fetch(swUrl, { method: 'HEAD' });
        if (!response.ok) {
          console.error('❌ Service worker file not accessible:', swUrl, 'Status:', response.status);
          return null;
        }
      } catch (fetchError) {
        console.error('❌ Error checking service worker file:', fetchError);
      }
      
      // Register service worker
      try {
        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/'
        });
        console.log('✅ Service Worker registered successfully');
      } catch (registerError) {
        console.error('❌ Error registering service worker:', registerError);
        return null;
      }
    } else {
      console.log('✅ Service Worker already registered');
    }

    // Wait for service worker to be ready
    registration = await navigator.serviceWorker.ready;
    
    if (!registration) {
      console.error('❌ Service Worker registration is null');
      return null;
    }

    // Wait for pushManager to be available
    let retries = 10;
    while (retries > 0 && (!registration.pushManager)) {
      await new Promise(resolve => setTimeout(resolve, 200));
      registration = await navigator.serviceWorker.getRegistration('/');
      retries--;
    }
    
    if (!registration || !registration.pushManager) {
      console.error('❌ Push Manager not available');
      return null;
    }
    
    return registration;
  } catch (error) {
    console.error('❌ Error in ensureServiceWorkerRegistered:', error);
    return null;
  }
};

/**
 * Get FCM Token for push notifications
 * @returns {Promise<string|null>} FCM token or null
 */
export const getFCMToken = async () => {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      console.warn('⚠️ Service Worker or Notification API not supported');
      return null;
    }

    if (!messaging) {
      console.warn('⚠️ Firebase messaging not initialized');
      return null;
    }

    if (!FCM_VAPID_KEY || FCM_VAPID_KEY === 'YOUR_VAPID_KEY_HERE') {
      console.warn('⚠️ FCM VAPID Key not configured');
      return null;
    }

    const registration = await ensureServiceWorkerRegistered();
    
    if (!registration) {
      console.warn('⚠️ Service Worker registration failed');
      return null;
    }

    if (!registration.pushManager) {
      console.warn('⚠️ Push Manager not available');
      return null;
    }

    if (!registration.active) {
      console.error('❌ Service worker is not active');
      return null;
    }

    // Wait a bit for service worker to be fully ready
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const currentRegistration = await navigator.serviceWorker.getRegistration('/');
    if (!currentRegistration || !currentRegistration.pushManager) {
      return null;
    }
    
    await navigator.serviceWorker.ready;
    
    // Get FCM token
    let token;
    try {
      token = await getToken(messaging, {
        vapidKey: FCM_VAPID_KEY,
        serviceWorkerRegistration: currentRegistration
      });
    } catch (tokenError) {
      console.error('❌ Error getting FCM token:', tokenError);
      throw tokenError;
    }

    if (token) {
      localStorage.setItem('fcmToken', token);
      console.log('✅ FCM Token obtained:', token);
      return token;
    } else {
      console.warn('⚠️ No registration token available. Request permission to generate one.');
      return null;
    }
  } catch (error) {
    console.error('❌ Error in getFCMToken:', error);
    return null;
  }
};

/**
 * Initialize push notifications
 * @returns {Promise<string|null>} FCM token or null
 */
export const initializePushNotifications = async () => {
  try {
    // Check if token already exists in localStorage
    const storedToken = localStorage.getItem('fcmToken');
    if (storedToken) {
      console.log('✅ Using stored FCM token');
      return storedToken;
    }

    // Request permission
    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
      console.warn('⚠️ Notification permission not granted. Permission status:', permission);
      if (permission === 'denied') {
        console.warn('⚠️ User has denied notification permission. They need to enable it in browser settings.');
      }
      return null;
    }

    // Get FCM token
    const token = await getFCMToken();
    
    if (token) {
      localStorage.setItem('notificationPermissionRequested', 'true');
      console.log('✅ Push notifications initialized successfully');
      return token;
    } else {
      console.warn('⚠️ Failed to get FCM token even though permission was granted');
    }

    return null;
  } catch (error) {
    console.error('❌ Error initializing push notifications:', error);
    return null;
  }
};

/**
 * Setup foreground message listener (when app is open)
 * @param {Function} onNotificationReceived - Callback function when notification is received
 */
export const setupForegroundMessageListener = (onNotificationReceived = null) => {
  if (!messaging) {
    console.warn('⚠️ Firebase messaging is not initialized');
    return;
  }

  onMessage(messaging, (payload) => {
    console.log('📬 Foreground message received:', payload);
    
    const title = payload.notification?.title || payload.data?.title || 'New Notification';
    const body = payload.notification?.body || payload.data?.body || 'You have a new notification';
    const icon = payload.notification?.icon || payload.data?.icon || '/favicon.ico';
    const image = payload.notification?.image || payload.data?.image;
    const tag = payload.data?.tag || 'default-notification';
    
    // Show browser notification if permission is granted
    if (Notification.permission === 'granted') {
      const notificationOptions = {
        body: body,
        icon: icon,
        badge: '/favicon.ico',
        image: image,
        data: payload.data || {},
        requireInteraction: false,
        tag: tag,
        ...payload.notification
      };

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, notificationOptions);
        }).catch((error) => {
          console.error('❌ Error showing notification via service worker:', error);
          new Notification(title, notificationOptions);
        });
      } else {
        new Notification(title, notificationOptions);
      }
    }
    
    // Call custom callback if provided
    if (onNotificationReceived && typeof onNotificationReceived === 'function') {
      onNotificationReceived(payload);
    }
  });
};

/**
 * Check if permission has been requested before
 * @returns {boolean}
 */
export const hasRequestedPermission = () => {
  return localStorage.getItem('notificationPermissionRequested') === 'true';
};

/**
 * Check if notifications are enabled
 * @returns {boolean}
 */
export const areNotificationsEnabled = () => {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'granted';
};

/**
 * Check if notification permission is denied
 * @returns {boolean}
 */
export const isNotificationPermissionDenied = () => {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'denied';
};

/**
 * Send FCM token to backend
 * @param {string} token - FCM token
 * @param {Function} apiCall - API function to send token
 * @returns {Promise<void>}
 */
export const sendTokenToBackend = async (token, apiCall) => {
  if (!token || !apiCall) {
    console.warn('⚠️ Token or API call function not provided');
    return;
  }

  try {
    await apiCall({ fcmToken: token });
    console.log('✅ FCM token sent to backend successfully');
  } catch (error) {
    console.error('❌ Error sending FCM token to backend:', error);
  }
};

/**
 * Delete FCM token and clear local storage
 * @returns {Promise<void>}
 */
export const deleteFCMToken = async () => {
  try {
    localStorage.removeItem('fcmToken');
    localStorage.removeItem('notificationPermissionRequested');
    console.log('✅ FCM token cleared from local storage');
  } catch (error) {
    console.error('❌ Error deleting FCM token:', error);
  }
};

